import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

async function syncQuoteNotice() {
  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    const notices = document.querySelectorAll('[data-quote-notice]');
    if (!notices.length) return;

    const quoteItems = cart.items.filter(i =>
      i.properties && (i.properties['_quote_required'] === 'true' || i.properties['Shipping quote'] === 'Charged separately')
    );
    const weightZeroItems = cart.items.filter(i =>
      i.grams === 0 && i.grams !== null
    );
    const standardItems = cart.items.filter(i =>
      (!i.properties || (i.properties['_quote_required'] !== 'true' && i.properties['Shipping quote'] !== 'Charged separately')) && !(i.grams === 0 && i.grams !== null)
    );
    const hasMixed = (quoteItems.length > 0 || weightZeroItems.length > 0) && standardItems.length > 0;
    const hasQuoteOnly = (quoteItems.length > 0 || weightZeroItems.length > 0) && standardItems.length === 0;

    notices.forEach(notice => {
      if (quoteItems.length === 0 && weightZeroItems.length === 0) {
        notice.style.display = 'none';
        return;
      }
      notice.style.display = 'block';
      const heading = notice.querySelector('.quote-notice__heading');
      const body = notice.querySelector('.quote-notice__body');
      if (hasMixed) {
        heading.textContent = 'Mixed cart — two shipping methods apply';
        body.textContent = 'Some of the items in your cart require custom shipping quotes to ensure we can offer you the best shipping rates available. Please proceed to check out, you can either request a quote or proceed with payment. We will contact you in 1 business day with a shipping quote. Do not worry if we are not able to get shipping for the right price, we are happy to refund any amount paid.';
      } else if (hasQuoteOnly) {
        heading.textContent = 'Shipping quote required';
        body.textContent = 'Some of the items in your cart require custom shipping quotes to ensure we can offer you the best shipping rates available. Please proceed to check out, you can either request a quote or proceed with payment. We will contact you in 1 business day with a shipping quote. Do not worry if we are not able to get shipping for the right price, we are happy to refund any amount paid.';
      }
    });
  } catch (e) {
    console.warn('Quote notice sync error:', e);
  }
}

document.addEventListener('DOMContentLoaded', syncQuoteNotice);
document.addEventListener('cart:updated', syncQuoteNotice);
document.addEventListener('cart:refresh', syncQuoteNotice);